import React, { useState } from 'react';

const SimpleTestWidget: React.FC = () => {
  const [testData, setTestData] = useState<string>('Test data loaded successfully');
  
  return (
    <div className="p-4 bg-slate-800 rounded-lg border border-slate-600">
      <h3 className="text-white font-semibold mb-2">Simple Test Widget</h3>
      <p className="text-slate-300">{testData}</p>
      <button 
        className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        onClick={() => setTestData('Button clicked at ' + new Date().toLocaleTimeString())}
      >
        Test Button
      </button>
    </div>
  );
};

const TimingTestPage: React.FC = () => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">Process Timing Widgets Test</h1>
          <p className="text-slate-300 text-lg">Testing ProcessTimingWidget and QueueTimingWidget components</p>
        </div>

        {/* Simple Test First */}
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-white mb-4">Basic Component Test</h2>
          <SimpleTestWidget />
          
          <button 
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced Timing Widgets
          </button>
        </div>

        {/* Advanced widgets - only show if basic test works */}
        {showAdvanced && (
          <div className="space-y-8">
            <div className="p-6 bg-yellow-900/20 border border-yellow-600/50 rounded-lg">
              <h3 className="text-yellow-400 font-semibold mb-2">⚠️ Advanced Widget Loading</h3>
              <p className="text-slate-300">
                Loading ProcessTimingWidget and QueueTimingWidget components...
                If you see a JavaScript error in the console, there may be a compatibility issue.
              </p>
            </div>
            
            {/* Lazy load the timing widgets to isolate errors */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold text-white mb-4">ProcessTimingWidget Tests</h2>
                <div className="p-4 bg-slate-800 rounded-lg">
                  <p className="text-slate-300">ProcessTimingWidget would load here</p>
                  <p className="text-xs text-slate-500 mt-2">
                    Component temporarily disabled to isolate charCodeAt error
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-semibold text-white mb-4">QueueTimingWidget Tests</h2>
                <div className="p-4 bg-slate-800 rounded-lg">
                  <p className="text-slate-300">QueueTimingWidget would load here</p>
                  <p className="text-xs text-slate-500 mt-2">
                    Component temporarily disabled to isolate charCodeAt error
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Test Instructions */}
        <div className="mt-12 p-6 bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-lg">
          <h2 className="text-xl font-semibold text-white mb-4">Debugging Information</h2>
          <div className="text-slate-300 space-y-2">
            <p>• <strong>Step 1:</strong> Verify basic React components render without errors</p>
            <p>• <strong>Step 2:</strong> Check browser console for JavaScript errors</p>
            <p>• <strong>Step 3:</strong> Gradually enable timing widgets to isolate issues</p>
            <p>• <strong>Current Status:</strong> Testing basic component rendering first</p>
          </div>
        </div>

        {/* API Status */}
        <div className="mt-8 p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
          <h3 className="text-lg font-semibold text-white mb-2">Environment Status</h3>
          <p className="text-slate-300 text-sm">
            React: {React.version || 'Unknown'} | 
            Environment: {typeof window !== 'undefined' ? 'Browser' : 'Server'} |
            Timestamp: {new Date().toISOString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TimingTestPage;
